import { createTheme } from '@mui/material';

/**
 * Tema da loja Inovart Gold: sistema, não estilo da landing.
 * Tokens aqui. Componentes de página só referenciam tokens.
 */

const palette = {
    ink: '#0B0A08',
    gold: '#C8A459',
    goldDark: '#9E7C38',
    goldSoft: '#F8F1DE',
    canvas: '#F6F5F1',
    paper: '#FFFFFF',
    text: '#1C1813',
    muted: '#6E665A',
    line: '#E5E2DA',
    success: '#2F6B45',
    warning: '#9C4B1B',
    error: '#B23A2E',
    info: '#215E54',
};

const elevation = ['none'];
for (let i = 1; i < 25; i++) {
    const y = Math.max(1, Math.round(i * 0.8));
    const blur = Math.max(2, Math.round(i * 1.4));
    const alpha = (0.04 + i * 0.006).toFixed(3);
    elevation.push(`0px ${y}px ${blur}px rgba(1, 1, 1, ${alpha})`);
}

let theme = createTheme({
    palette: {
        primary: {
            main: palette.ink,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: palette.gold,
            dark: palette.goldDark,
            light: palette.goldSoft,
            contrastText: '#FFFFFF',
        },
        text: {
            primary: palette.text,
            secondary: palette.muted,
        },
        background: {
            default: palette.canvas,
            paper: palette.paper,
        },
        divider: palette.line,
        success: { main: palette.success },
        warning: { main: palette.warning },
        error: { main: palette.error },
        info: { main: palette.info },
    },
    shape: {
        borderRadius: 8,
    },
    shadows: elevation,
});

theme = createTheme(theme, {
    typography: {
        fontFamily: 'Montserrat',
        h1: {
            fontFamily: 'DM Serif Display',
            fontWeight: 400,
            fontSize: '2rem',
            lineHeight: 1.1,
            textWrap: 'pretty',
            [theme.breakpoints.up('lg')]: {
                fontSize: '4rem',
            },
        },
        h2: {
            fontFamily: 'DM Serif Display',
            fontWeight: 400,
            fontSize: '1.875rem',
            lineHeight: 1.15,
            textWrap: 'pretty',
            [theme.breakpoints.up('lg')]: {
                fontSize: '2.5rem',
            },
        },
        h3: {
            fontFamily: 'DM Serif Display',
            fontWeight: 400,
            fontSize: '1.5rem',
            lineHeight: 1.15,
            textWrap: 'pretty',
            [theme.breakpoints.up('lg')]: {
                fontSize: '1.75rem',
            },
        },
        h4: {
            fontFamily: 'DM Serif Display',
            fontWeight: 400,
            fontSize: '1.25rem',
            lineHeight: 1.2,
            textWrap: 'pretty',
        },
        subtitle1: {
            fontFamily: 'DM Serif Display',
            fontSize: '1rem',
            fontWeight: 400,
            [theme.breakpoints.up('lg')]: {
                fontSize: '1.25rem',
            },
        },
        subtitle2: {
            fontFamily: 'Montserrat',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: palette.gold,
        },
        body1: {
            fontFamily: 'Montserrat',
            fontSize: '1rem',
            lineHeight: 1.6,
            textWrap: 'pretty',
        },
        body2: {
            fontFamily: 'Montserrat',
            fontSize: '0.875rem',
            lineHeight: 1.5,
        },
        button: {
            fontFamily: 'Montserrat',
            fontWeight: 600,
            fontSize: '0.875rem',
            textTransform: 'none',
        },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: palette.canvas,
                    color: palette.text,
                },
                'h1, h2, h3, h4': {
                    textWrap: 'pretty',
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    fontFamily: 'Montserrat',
                    textDecoration: 'none',
                    transition: 'color .2s ease-out',
                    '&:hover': {
                        color: theme.palette.secondary.main,
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 6,
                    minHeight: 40,
                    padding: '8px 20px',
                    transition: 'background-color .15s ease-out, border-color .15s ease-out, color .15s ease-out',
                    '&:focus-visible': {
                        outline: `2px solid ${palette.gold}`,
                        outlineOffset: 2,
                    },
                },
                sizeSmall: {
                    minHeight: 36,
                    padding: '6px 16px',
                    fontSize: '0.8125rem',
                },
                sizeLarge: {
                    minHeight: 48,
                    padding: '10px 40px',
                    fontSize: '1rem',
                },
                containedSecondary: {
                    '&:hover': {
                        backgroundColor: palette.goldDark,
                    },
                },
                outlinedPrimary: {
                    borderWidth: 1.5,
                    borderColor: palette.ink,
                    '&:hover': {
                        borderWidth: 1.5,
                        borderColor: palette.ink,
                        backgroundColor: 'transparent',
                        color: palette.goldDark,
                    },
                },
                textSecondary: {
                    '&:hover': {
                        backgroundColor: 'transparent',
                        color: palette.goldDark,
                    },
                },
            },
        },
        MuiCard: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                    border: `1px solid ${palette.line}`,
                    backgroundColor: palette.paper,
                },
            },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                outlined: {
                    borderColor: palette.line,
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: 6,
                    backgroundColor: palette.paper,
                    '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.line,
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.gold,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: palette.gold,
                        borderWidth: 1.5,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    fontWeight: 600,
                },
                outlined: {
                    borderColor: palette.line,
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                separator: {
                    color: palette.muted,
                },
            },
        },
        MuiCheckbox: {
            defaultProps: {
                color: 'secondary',
            },
        },
        MuiCircularProgress: {
            defaultProps: {
                color: 'secondary',
            },
        },
    },
});

export default theme;
